import { memo, type FC } from "react";
import type { ToolCall } from "./ndjsonTypes";
import {
  humanizeToolName,
  formatToolArgs,
  parseToolResult,
  type ParsedToolResult
} from "./ndjsonToolUtils";
import { MarkdownContent } from "./markdown";
import {
  ToolCallListRoot,
  ToolCallDetails,
  ToolCallSummary,
  Chevron,
  StatusIcon,
  ToolCallName,
  ToolCallBody,
  SectionLabel,
  CodeBlock,
  ProseResult,
  ResponseScroll,
  DocChunkDetails,
  DocChunkSummary,
  DocChunkContent,
  ToolCallSpinner
} from "./ToolCallSection.styles";

interface DocChunk {
  title: string;
  content: string;
}

const ChevronIcon: FC = () => (
  <svg viewBox="0 0 16 16" fill="currentColor">
    <path d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06Z" />
  </svg>
);

const parseDocChunks = (text: string): DocChunk[] => {
  const chunks: DocChunk[] = [];
  const parts = text.split(/\n---\s+(.+?)\s+---\n/);
  for (let i = 1; i < parts.length; i += 2) {
    const title = parts[i];
    const content = (parts[i + 1] ?? "").trim();
    if (title) {
      chunks.push({ title, content });
    }
  }
  return chunks;
};

const ToolCallResponse: FC<{ result: ParsedToolResult }> = ({ result }) => {
  const docChunks = parseDocChunks(result.text);

  if (docChunks.length > 0) {
    return (
      <ResponseScroll>
        {docChunks.map((chunk, i) => (
          <DocChunkDetails key={i}>
            <DocChunkSummary>
              <Chevron>
                <ChevronIcon />
              </Chevron>
              {chunk.title}
            </DocChunkSummary>
            <DocChunkContent>{chunk.content}</DocChunkContent>
          </DocChunkDetails>
        ))}
      </ResponseScroll>
    );
  }

  if (result.isProse) {
    return (
      <ProseResult>
        <MarkdownContent text={result.text} />
      </ProseResult>
    );
  }

  return <CodeBlock>{result.text}</CodeBlock>;
};

const ToolCallItem: FC<{ tc: ToolCall }> = ({ tc }) => {
  const isRunning = tc.status === "running";
  const result = tc.result ? parseToolResult(tc.result) : undefined;
  const args = formatToolArgs(tc.arguments);

  return (
    <ToolCallDetails>
      <ToolCallSummary>
        <Chevron>
          <ChevronIcon />
        </Chevron>
        <StatusIcon status={isRunning ? "running" : "completed"}>
          {isRunning ? <ToolCallSpinner /> : "✓"}
        </StatusIcon>
        <ToolCallName>{humanizeToolName(tc.name)}</ToolCallName>
      </ToolCallSummary>
      <ToolCallBody>
        <div>
          <SectionLabel>Call</SectionLabel>
          <CodeBlock>
            {tc.name}({args})
          </CodeBlock>
        </div>
        {result && (
          <div>
            <SectionLabel>Response</SectionLabel>
            <ToolCallResponse result={result} />
          </div>
        )}
      </ToolCallBody>
    </ToolCallDetails>
  );
};

export const ToolCallSection: FC<{ toolCalls: ToolCall[] }> = memo(
  ({ toolCalls }) => {
    if (toolCalls.length === 0) {
      return null;
    }

    return (
      <ToolCallListRoot>
        {toolCalls.map((tc, i) => (
          <ToolCallItem key={`${tc.name}-${i}`} tc={tc} />
        ))}
      </ToolCallListRoot>
    );
  }
);
